import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Search, X } from "lucide-react";

export default function SearchableDropdown({
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  disabled = false,
  showOther = true,
  otherPlaceholder = "Enter custom value",
  className = "",
  maxHeight = "200px"
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if search term doesn't match any option
  const hasExactMatch = options.some(option => 
    option.toLowerCase() === searchTerm.toLowerCase()
  );

  // Show "Other" option when search doesn't match existing options
  const shouldShowOther = showOther && searchTerm && !hasExactMatch && filteredOptions.length === 0;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
        setShowCustomInput(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    if (option === "Other") {
      setShowCustomInput(true);
      setSearchTerm("");
    } else {
      onChange(option);
      setIsOpen(false);
      setSearchTerm("");
      setShowCustomInput(false);
    }
  };

  const handleCustomSubmit = () => {
    if (customValue.trim()) {
      onChange(customValue.trim());
      setIsOpen(false);
      setSearchTerm("");
      setShowCustomInput(false);
      setCustomValue("");
    }
  };

  const handleClear = () => {
    onChange("");
    setSearchTerm("");
    setShowCustomInput(false);
    setCustomValue("");
  };

  const displayValue = value || placeholder;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-4 py-3 rounded-xl border text-left transition-all ${
          disabled
            ? "border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed"
            : isOpen
            ? "border-emerald-400 ring-2 ring-emerald-100"
            : "border-gray-300 hover:border-emerald-300 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
        }`}
      >
        <div className="flex items-center justify-between">
          <span className={value ? "text-gray-900" : "text-gray-500"}>
            {displayValue}
          </span>
          <div className="flex items-center gap-2">
            {value && !disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X size={14} className="text-gray-400" />
              </button>
            )}
            <ChevronDown 
              size={16} 
              className={`text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} 
            />
          </div>
        </div>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg"
          >
            {/* Search Input */}
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none"
                />
              </div>
            </div>

            {/* Options List */}
            <div 
              className="max-h-48 overflow-y-auto"
              style={{ maxHeight }}
            >
              {showCustomInput ? (
                <div className="p-3 border-b border-gray-100">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Custom Value
                      </label>
                      <input
                        type="text"
                        placeholder={otherPlaceholder}
                        value={customValue}
                        onChange={(e) => setCustomValue(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleCustomSubmit();
                          } else if (e.key === "Escape") {
                            setShowCustomInput(false);
                            setCustomValue("");
                          }
                        }}
                        autoFocus
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleCustomSubmit}
                        disabled={!customValue.trim()}
                        className="flex-1 px-3 py-2 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                      <button
                        onClick={() => {
                          setShowCustomInput(false);
                          setCustomValue("");
                        }}
                        className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Filtered Options */}
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelect(option)}
                        className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 transition-colors ${
                          value === option ? "bg-emerald-50 text-emerald-700" : "text-gray-900"
                        }`}
                      >
                        {option}
                      </button>
                    ))
                  ) : searchTerm ? (
                    <div className="px-4 py-3 text-sm text-gray-500">
                      No options found for "{searchTerm}"
                    </div>
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500">
                      No options available
                    </div>
                  )}

                  {/* Other Option */}
                  {shouldShowOther && (
                    <button
                      type="button"
                      onClick={() => handleSelect("Other")}
                      className="w-full px-4 py-3 text-left text-sm text-emerald-600 hover:bg-emerald-50 border-t border-gray-100 font-medium"
                    >
                      + Add "{searchTerm}" as custom option
                    </button>
                  )}

                  {/* Show Other Option */}
                  {showOther && !searchTerm && filteredOptions.length > 0 && (
                    <button
                      type="button"
                      onClick={() => handleSelect("Other")}
                      className="w-full px-4 py-3 text-left text-sm text-emerald-600 hover:bg-emerald-50 border-t border-gray-100 font-medium"
                    >
                      + Other
                    </button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
